import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * Button — the kit's `.btn` / `.btn-*`.
 *
 * 48px minimum by default, which is the kit's touch size. kit.css narrows it
 * contextually rather than through a prop — 44px inside `.topbar`, `.pane-foot`
 * and `.dmodal`, tighter padding inside `.sheet-foot`. That is deliberate: the
 * size follows where the button sits, so a caller never picks it by hand and
 * two buttons in the same bar cannot disagree.
 */

export type ButtonKind = 'primary' | 'outline' | 'ghost' | 'danger' | 'warnline';

const KIND_CLASS: Record<ButtonKind, string> = {
  primary: 'btn-primary',
  outline: 'btn-outline',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
  warnline: 'btn-warnline',
};

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** `style` rides through from ButtonHTMLAttributes — the canvases use it for
   *  the flex ratios in a button pair (Full profile 1 : Start visit 1.3). */
  children: ReactNode;
  kind?: ButtonKind;
  /** Leading icon or glyph. The kit spaces it with an 8px gap. */
  icon?: ReactNode;
  className?: string;
}

export function Button({
  children,
  kind = 'primary',
  icon,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={clsx('btn', KIND_CLASS[kind], className)} {...rest}>
      {icon}
      {children}
    </button>
  );
}
